import { ArchCourseForTable } from 'apis/entities/architecture.entity'
import sortSvg from 'images/sort.svg'
import sort2Svg from 'images/sort2.svg'
import { useMemo, useState } from 'react'
import { Tooltip } from 'react-tooltip'
import Spinner from './Spinner'
import Emitter, { Events } from 'core/emitter'
import excelSvg from 'images/excel.svg'
import {
  Direction,
  DirectionAsc,
  DirectionDesc,
  alternateStyle,
  tooltipStyle,
  tooltipStyleReverse,
} from 'utils/TableUtils'
import { useArchitectureApi } from 'providers/ArchitectureApiProvider'
import { getExportFileNameForCourse } from 'utils/ArchUtil'
import { downloadBlobAsFile } from 'utils/DownloadUtil'

enum SortBy {
  Client = 'client',
  Program = 'program',
  Intake = 'cohort',
  Course = 'course',
}

interface ArchReadOnlyViewProps {
  courses: ArchCourseForTable[]
  isLoading: boolean
  filteredProgramName?: string
  filteredIntakeName?: string
  isEnterprise?: boolean
}

interface ViewDetailsButtonProps {
  courseName: string
  fill: string
  onClick: () => void
}

function ViewDetailsButton({
  courseName,
  fill,
  onClick,
}: ViewDetailsButtonProps) {
  return (
    <button
      type="button"
      className="flex min-w-[17px] cursor-pointer items-center justify-center border-0 bg-transparent p-0"
      aria-label={`View details for ${courseName}`}
      data-tooltip-id="tooltip-view-details"
      onClick={onClick}
    >
      <svg
        width="17"
        height="13"
        viewBox="0 0 17 13"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M12 0H17V6H12V0ZM6 6V0H11V6H6ZM12 13V7H17V13H12ZM6 13V7H11V13H6ZM0 13V7H5V13H0ZM0 6V0H5V6H0ZM1 1V5H4V1H1ZM7 1V5H10V1H7ZM13 1V5H16V1H13ZM1 8V12H4V8H1ZM7 8V12H10V8H7ZM13 8V12H16V8H13Z"
          fill={fill}
        />
      </svg>
    </button>
  )
}

export default function ArchReadOnlyView({
  courses,
  isLoading,
  filteredProgramName,
  filteredIntakeName,
  isEnterprise,
}: ArchReadOnlyViewProps) {
  const { exportArchExcel } = useArchitectureApi()
  const [sortConfig, setSortConfig] = useState<{
    key: SortBy
    direction: Direction
  } | null>({ key: SortBy.Client, direction: DirectionAsc })
  const [orderByClient, setOrderByClient] = useState<Direction>('')
  const [orderByProgram, setOrderByProgram] = useState<Direction>('')
  const [orderByIntake, setOrderByIntake] = useState<Direction>('')
  const [orderByCourse, setOrderByCourse] = useState<Direction>('')

  const sortedCourses = useMemo(() => {
    if (!sortConfig) {
      return courses
    }

    // filter courses by program and intake if any
    let tmpCourses = [...courses]
    if (filteredProgramName) {
      tmpCourses = tmpCourses.filter(
        (c) => c.programName === filteredProgramName,
      )
    }
    // console.log('filteredIntakeName:', filteredIntakeName)
    if (filteredIntakeName) {
      tmpCourses = tmpCourses.filter((c) => c.intakeName === filteredIntakeName)
    }

    const sortedCourses = [...tmpCourses]
    switch (sortConfig.key) {
      case SortBy.Client:
        sortedCourses.sort((a, b) => {
          if (a.clientName < b.clientName) {
            return sortConfig.direction === DirectionAsc ? -1 : 1
          }
          if (a.clientName > b.clientName) {
            return sortConfig.direction === DirectionAsc ? 1 : -1
          }
          return 0
        })
        break
      case SortBy.Program:
        sortedCourses.sort((a, b) => {
          if (a.programName < b.programName) {
            return sortConfig.direction === DirectionAsc ? -1 : 1
          }
          if (a.programName > b.programName) {
            return sortConfig.direction === DirectionAsc ? 1 : -1
          }
          return 0
        })
        break
      case SortBy.Intake:
        sortedCourses.sort((a, b) => {
          if (a.intakeName < b.intakeName) {
            return sortConfig.direction === DirectionAsc ? -1 : 1
          }
          if (a.intakeName > b.intakeName) {
            return sortConfig.direction === DirectionAsc ? 1 : -1
          }
          return 0
        })
        break
      case SortBy.Course:
        sortedCourses.sort((a, b) => {
          if (a.name < b.name) {
            return sortConfig.direction === DirectionAsc ? -1 : 1
          }
          if (a.name > b.name) {
            return sortConfig.direction === DirectionAsc ? 1 : -1
          }
          return 0
        })
        break
      default:
        break
    }

    if (sortConfig.key !== SortBy.Course) {
      // sort by createdAt if client name is the same
      sortedCourses.sort((a, b) => {
        if (a.clientName === b.clientName) {
          // date comparison
          const dateA = new Date(a.createdAt).getTime()
          const dateB = new Date(b.createdAt).getTime()
          return dateB - dateA
        }
        return 0
      })
    }

    return sortedCourses
  }, [courses, filteredIntakeName, filteredProgramName, sortConfig])

  const onClickSort = (key: SortBy) => {
    let ascending = true
    if (
      sortConfig &&
      sortConfig.key === key &&
      sortConfig.direction === DirectionAsc
    ) {
      ascending = false
    }

    switch (key) {
      case SortBy.Client:
        setOrderByClient(ascending ? DirectionAsc : DirectionDesc)
        break
      case SortBy.Program:
        setOrderByProgram(ascending ? DirectionAsc : DirectionDesc)
        break
      case SortBy.Intake:
        setOrderByIntake(ascending ? DirectionAsc : DirectionDesc)
        break
      case SortBy.Course:
        setOrderByCourse(ascending ? DirectionAsc : DirectionDesc)
        break
      default:
        break
    }

    setSortConfig({ key, direction: ascending ? DirectionAsc : DirectionDesc })
  }

  const getTooltipStyle = (key: SortBy): any => {
    switch (key) {
      case SortBy.Client:
        return orderByClient === DirectionAsc
          ? tooltipStyleReverse
          : tooltipStyle
      case SortBy.Program:
        return orderByProgram === DirectionAsc
          ? tooltipStyleReverse
          : tooltipStyle
      case SortBy.Intake:
        return orderByIntake === DirectionAsc
          ? tooltipStyleReverse
          : tooltipStyle
      case SortBy.Course:
        return orderByCourse === DirectionAsc
          ? tooltipStyleReverse
          : tooltipStyle

      default:
        return tooltipStyle
    }
  }

  const getSortingText = (key: SortBy): string => {
    switch (key) {
      case SortBy.Client:
        if (orderByClient === DirectionAsc) return 'Sort Z to A'
        return 'Sort A to Z'
      case SortBy.Program:
        if (orderByProgram === DirectionAsc) return 'Sort Z to A'
        return 'Sort A to Z'
      case SortBy.Intake:
        if (orderByIntake === DirectionAsc) return 'Sort Z to A'
        return 'Sort A to Z'
      case SortBy.Course:
        if (orderByCourse === DirectionAsc) return 'Sort Z to A'
        return 'Sort A to Z'
      default:
        return ''
    }
  }

  const onClickViewDetails = (course: ArchCourseForTable) => {
    Emitter.emit(Events.ShowArchCourseDetails, { course })
  }

  const onClickExport = async (course: ArchCourseForTable) => {
    try {
      const filename = getExportFileNameForCourse(course.name)
      const res = await exportArchExcel(
        course.programId,
        course.intakeId,
        course.id,
      )
      downloadBlobAsFile(res, filename)
    } catch (error) {
      console.log('Error exporting excel', error)
    }
  }

  const renderRow = (course: ArchCourseForTable) => {
    return (
      <tr key={course.id}>
        {isEnterprise && (
          <td className={`${alternateStyle('td', true)}`}>
            <div className="flex items-center justify-center px-[16px]">
              {course.clientName}
            </div>
          </td>
        )}
        <td className={alternateStyle('td', !isEnterprise)}>
          <div className="flex items-center justify-center px-[16px]">
            {course.programName}
          </div>
        </td>
        <td
          className={`${alternateStyle(
            'td',
          )} !whitespace-normal !overflow-visible`}
        >
          <div className="flex items-center justify-center gap-1 px-[16px]">
            <div className="grow text-center">{course.intakeName}</div>
            {!isEnterprise && (
              <>
                <div className="w-[16px]" />
                <ViewDetailsButton
                  courseName={course.name}
                  fill="#007882"
                  onClick={() => onClickViewDetails(course)}
                />
              </>
            )}
          </div>
        </td>
        {isEnterprise && (
          <td className={alternateStyle('td')}>
            <div className="flex items-center justify-center gap-1 px-[16px]">
              <div className="grow text-center">{course.name}</div>
              <div className="w-[16px]" />
              <ViewDetailsButton
                courseName={course.name}
                fill="#8a68c8"
                onClick={() => onClickViewDetails(course)}
              />
            </div>
          </td>
        )}
        <td className={alternateStyle('td')}>
          <div className="flex items-center justify-center">
            <button
              type="button"
              className="flex cursor-pointer items-center justify-center border-0 bg-transparent p-0"
              aria-label={`Export ${course.name}`}
              onClick={() => onClickExport(course)}
            >
              <img src={excelSvg} alt="" />
            </button>
          </div>
        </td>
      </tr>
    )
  }

  return (
    <div className="flex flex-col">
      {isLoading && <Spinner />}
      {!isLoading && sortedCourses.length === 0 && (
        <div className="flex flex-1 items-start justify-center">
          No data available
        </div>
      )}
      {!isLoading && sortedCourses.length > 0 && (
        <>
          <div className="mentem-scrollbar-2 max-h-[60vh] overflow-x-clip overflow-y-auto">
            <table className="mentem-table mentem-table-highlight">
              <thead>
                <tr>
                  {isEnterprise && (
                    <th
                      className={`${alternateStyle(
                        'th',
                        true,
                      )} h-[55px] w-[200px]`}
                      style={{
                        zIndex: 2,
                      }}
                    >
                      <div className="flex flex-row items-center">
                        <span className="grow">Client</span>
                        <img
                          className="cursor-pointer"
                          src={sortSvg}
                          alt="sort"
                          onClick={() => onClickSort(SortBy.Client)}
                          data-tooltip-id="tooltip-client"
                        />
                      </div>
                    </th>
                  )}
                  <th className={`${alternateStyle('th', !isEnterprise)}`}>
                    <div className="flex flex-row items-center">
                      <span className="grow">
                        {isEnterprise ? 'Program' : 'Course'}
                      </span>
                      <img
                        className="cursor-pointer"
                        src={isEnterprise ? sort2Svg : sortSvg}
                        alt="sort"
                        onClick={() => onClickSort(SortBy.Program)}
                        data-tooltip-id="tooltip-program"
                      />
                    </div>
                  </th>
                  <th
                    className={`${alternateStyle(
                      'th',
                    )} !whitespace-normal !overflow-visible${
                      isEnterprise ? ' !w-[125px]' : ''
                    }`}
                  >
                    <div className="flex flex-row items-center">
                      <span className="grow">
                        {isEnterprise ? 'Cohort' : 'Hexamester'}
                      </span>
                      <img
                        className="cursor-pointer"
                        src={isEnterprise ? sortSvg : sort2Svg}
                        alt="sort"
                        onClick={() => onClickSort(SortBy.Intake)}
                        data-tooltip-id="tooltip-intake"
                      />
                    </div>
                  </th>
                  {isEnterprise && (
                    <th className={`${alternateStyle('th')}`}>
                      <div className="flex flex-row items-center">
                        <span className="grow">Course</span>
                        <img
                          className="cursor-pointer"
                          src={sort2Svg}
                          alt="sort"
                          onClick={() => onClickSort(SortBy.Course)}
                          data-tooltip-id="tooltip-course"
                        />
                      </div>
                    </th>
                  )}
                  <th className={`${alternateStyle('th')} w-[155px]`}>
                    <div className="flex flex-row items-center">
                      <span className="grow">Export</span>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedCourses.map((course: ArchCourseForTable) => {
                  return renderRow(course)
                })}
              </tbody>
            </table>
            <Tooltip
              id="tooltip-view-details"
              className="mentem-tooltip"
              style={tooltipStyle}
              place="top"
              noArrow={true}
            >
              View details
            </Tooltip>
            <Tooltip
              id="tooltip-client"
              className="mentem-tooltip"
              style={getTooltipStyle(SortBy.Client)}
              place="top"
              noArrow={true}
            >
              {getSortingText(SortBy.Client)}
            </Tooltip>
            <Tooltip
              id="tooltip-program"
              className="mentem-tooltip"
              style={getTooltipStyle(SortBy.Program)}
              place="top"
              noArrow={true}
            >
              {getSortingText(SortBy.Program)}
            </Tooltip>
            <Tooltip
              id="tooltip-intake"
              className="mentem-tooltip"
              style={getTooltipStyle(SortBy.Intake)}
              place="top"
              noArrow={true}
            >
              {getSortingText(SortBy.Intake)}
            </Tooltip>
            <Tooltip
              id="tooltip-course"
              className="mentem-tooltip"
              style={getTooltipStyle(SortBy.Course)}
              place="top"
              noArrow={true}
            >
              {getSortingText(SortBy.Course)}
            </Tooltip>
          </div>
        </>
      )}
    </div>
  )
}
